import React from 'react';

import classNames from 'clsx';
import PropTypes from 'prop-types';

import { noop } from '../../../default-props/noop';
import { GuidService } from '../../../services/guid-service';
import { testId } from '../../../services/testid-service';
import style from './input.module.css';

const toInputValue = (value) => {
  return value || value === 0 ? `${value}` : '';
};

export class Input extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasChanged: false };
  }

  onChange = (e) => {
    const { onChange, exposeNativeEventInCallbacks = false } = this.props;
    const value = exposeNativeEventInCallbacks ? e : e.target.value;
    onChange(value);
    this.setState({ hasChanged: true });
  };

  onBlur = (e) => {
    const { onBlur = noop, exposeNativeEventInCallbacks = false } = this.props;
    const value = exposeNativeEventInCallbacks ? e : e.target.value;
    onBlur(value);
  };

  onFocus = (e) => {
    const { onFocus = noop, exposeNativeEventInCallbacks = false } = this.props;
    const value = exposeNativeEventInCallbacks ? e : e.target.value;
    onFocus(value);
  };

  getValue = () => {
    const { value = '', defaultValue = 0 } = this.props;
    const { hasChanged } = this.state;
    if (!hasChanged && value === defaultValue) {
      return null;
    }
    return value;
  };

  identifier = GuidService.newGuid();

  render() {
    const {
      type = 'text',
      label,
      hasError = false,
      required = false,
      disabled = false,
      className = '',
      placeholder = '',
      autoComplete = false,
      setRef = noop,
      'data-testid': dataTestId,
      name,
      isPrivate,
      onKeyDown = noop,
    } = this.props;
    const value = this.getValue();
    const allClassName = classNames(
      style['flex-input-container'],
      { [style['without-label']]: !label },
      { [style.error]: hasError },
      { [style.disabled]: disabled },
      // setting this class hides the input from median - hellomedian.com ?
      { 'median-exclude-selector': isPrivate },
      className,
    );
    const inputId = dataTestId ? `${dataTestId}-input` : `${name}-input`;

    return (
      <div className={allClassName}>
        {!!label && (
          <label className={classNames(style['flex-label'], hasError ? style['has-error'] : '')} htmlFor={inputId}>
            {label}
            {required && <span className={style['required-label']}> &lowast;</span>}
          </label>
        )}
        <div className={style['flex-input-wrapper']}>
          <input
            ref={setRef}
            aria-label={this.props['aria-label']}
            aria-labelledby={this.props['aria-labelledby']}
            autoComplete={autoComplete ? null : 'off'}
            className={style['flex-input']}
            disabled={disabled}
            id={inputId}
            maxLength={this.props.maxLength}
            name={name}
            onBlur={this.onBlur}
            onChange={this.onChange}
            onFocus={this.onFocus}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            type={type}
            value={toInputValue(value || '')}
            {...testId(dataTestId)}
          />
        </div>
      </div>
    );
  }
}

Input.propTypes = {
  // Hyphenated format not recommended. Done here purely to keep in line with the new version's automagic props.
  'aria-label': PropTypes.string,
  'aria-labelledby': PropTypes.string,
  type: PropTypes.string,
  label: PropTypes.node,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
  className: PropTypes.string,
  maxLength: PropTypes.number,
  hasError: PropTypes.bool,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  isPrivate: PropTypes.bool,
  placeholder: PropTypes.string,
  autoComplete: PropTypes.bool,
  setRef: PropTypes.func,
  'data-testid': PropTypes.string,
  name: PropTypes.string,
  exposeNativeEventInCallbacks: PropTypes.bool,
  id: PropTypes.string,
  defaultValue: PropTypes.number,
};
